import ActionButton from '@/components/buttons/ActionButton.tsx';
import { CircleArrowRight, Loader } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/Drawer.tsx';
import { Card, CardContent } from '@/components/ui/Card.tsx';
import { useBookingQuote } from '@/features/public/booking/context/BookingQuoteContext.tsx';
import { useFormContext } from 'react-hook-form';
import { ClientInputFormWithCustomFields } from '@/features/public/booking/schemas/clientSchema.ts';
import React, { useState } from 'react';
import { getEstimatedPricesString } from '@/features/shared/pricing/util/PricingFormatter.ts';
import { getTemporalIdForIndex } from '@/features/shared/booking/utils/temporalId.ts';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import { cn } from '@/lib/utils.ts';
import useVirtualKeyboardOpen from '@/hooks/useVirtualKeyboardOpen.ts';

const BookingActionFooter = ({ onSubmit }: { onSubmit: () => void }) => {
  const [summaryOpen, setSummaryOpen] = useState(false);

  const clientForm = useFormContext<ClientInputFormWithCustomFields>();
  const clientFormData = clientForm.watch();
  const { isSubmitting } = clientForm.formState;
  const { quote, isFetching } = useBookingQuote();
  // Przy klawiaturze przyklejona stopka wisi nad nią i zasłania wypełniane
  // pole, a i tak nie ma wtedy nic do zrobienia — wraca po zejściu z pola.
  const keyboardOpen = useVirtualKeyboardOpen();

  // Wiersze wyceny w kolejności formularza — korelacja po temporalId (UUID).
  const orderedParticipants = (clientFormData.participants ?? []).map(
    (_, index) =>
      quote?.pricesPerParticipants?.find(
        (p) => p.temporalId === getTemporalIdForIndex(index),
      ),
  );

  const totalPriceString = getEstimatedPricesString(quote?.total);
  const handleSubmit = () => {
    onSubmit();
    setSummaryOpen(false);
  };

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 w-full border-t border-neutral-200 bg-white p-4 shadow-2xl',
        keyboardOpen && 'hidden',
      )}
    >
      <div className="mx-auto flex max-w-[1000px] flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-2">
        <div className="flex flex-row items-center gap-1 text-base-text">
          {'Cena całkowita:'}
          <Drawer open={summaryOpen} onOpenChange={setSummaryOpen}>
            <DrawerTrigger className="group flex flex-row items-center gap-1">
              {/* Pierwsza wycena jeszcze leci — kwota pojawi się za moment. */}
              {!quote && isFetching ? (
                <Skeleton className="h-5 w-24" />
              ) : (
                <p className="font-medium underline transition group-hover:opacity-85">
                  {totalPriceString}
                </p>
              )}
              <CircleArrowRight
                size={18}
                className="text-accent-600 transition group-hover:-rotate-90"
              />
              {isFetching && (
                <>
                  <Loader
                    className={cn('size-4 animate-spin text-neutral-900')}
                  />
                </>
              )}
            </DrawerTrigger>
            <DrawerContent aria-describedby={undefined}>
              <DrawerHeader className="">
                <DrawerTitle>Podsumowanie kosztów</DrawerTitle>
              </DrawerHeader>
              <div className="mx-auto w-full max-w-[600px] space-y-2 px-4 pb-6">
                <Card className="w-full">
                  <CardContent className="w-full">
                    {orderedParticipants.map((p, index) => (
                      <div
                        className="mb-2 flex flex-row justify-between"
                        key={index}
                      >
                        <div className="">Uczestnik {index + 1}</div>
                        <div className="font-bold">
                          {getEstimatedPricesString(p?.estimatedPrice)}
                        </div>
                      </div>
                    ))}
                    <hr className="my-1 text-neutral-200" />
                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Cena całkowita</div>
                      <div className="font-semibold">{totalPriceString}</div>
                    </div>
                  </CardContent>
                </Card>
                <div className="flex flex-col-reverse justify-between gap-2 md:flex-row">
                  <ActionButton
                    tone="outline"
                    size="block"
                    className="md:w-auto"
                    onClick={() => setSummaryOpen(false)}
                  >
                    Zamknij
                  </ActionButton>
                  <ActionButton
                    tone="accent"
                    size="block"
                    className="md:w-auto"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                  >
                    Przejdź do podsumowania
                  </ActionButton>
                </div>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
        <div className="flex w-full flex-row gap-2 md:w-auto">
          <ActionButton
            tone="accent"
            size="block"
            type="submit"
            isLoading={isSubmitting}
          >
            Przejdź do podsumowania
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

export default BookingActionFooter;
